import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { Save, Users } from 'lucide-react';
import { admin as adminApi } from '../utils/api';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const CapacityManager = () => {
  const [settings, setSettings] = useState({});
  const [sizeCategories, setSizeCategories] = useState([]);
  const [sizingEnabled, setSizingEnabled] = useState(true);
  const [capacityEnabled, setCapacityEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingToggle, setSavingToggle] = useState(false);

  useEffect(() => {
    loadData();
    
    // Reload data when size categories are updated
    const handleSizeCategoryUpdate = () => {
      console.log('[CapacityManager] sizeCategoryUpdated event received, reloading...');
      loadData();
    };

    window.addEventListener('sizeCategoryUpdated', handleSizeCategoryUpdate);
    
    return () => {
      window.removeEventListener('sizeCategoryUpdated', handleSizeCategoryUpdate);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    try {
      const token = sessionStorage.getItem('token');
      console.log('[CapacityManager] Loading data...');
      
      const [settingsRes, sizeCategoriesRes, sizingSettingsRes, capacityEnabledRes] = await Promise.all([
        adminApi.getSettings(),
        axios.get(`${API_URL}/size-categories`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API_URL}/size-categories/settings`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API_URL}/business-info/capacity_limits_enabled`, {
          headers: { Authorization: `Bearer ${token}` },
        }).catch(() => ({ data: { info_value: 'true' } })),
      ]);
      
      console.log('[CapacityManager] Size categories:', sizeCategoriesRes.data);
      console.log('[CapacityManager] First category structure:', sizeCategoriesRes.data[0]);
      console.log('[CapacityManager] Sizing enabled:', sizingSettingsRes.data.sizing_enabled);
      
      setSizeCategories(sizeCategoriesRes.data);
      setSizingEnabled(sizingSettingsRes.data.sizing_enabled === 'true');
      setCapacityEnabled(capacityEnabledRes.data.info_value !== 'false');
      
      // Load capacity settings and match with actual size categories
      const capacitySettings = settingsRes.data.filter(s => s.setting_key.startsWith('daily_capacity_'));
      
      // Build settings object based on CURRENT size categories only
      const newSettings = {};
      sizeCategoriesRes.data.forEach(cat => {
        // Create a clean key by sanitizing the category label
        // Remove everything in parentheses and special characters, convert to lowercase with underscores
        const cleanLabel = cat.category_label
          .replace(/\s*\([^)]*\)/g, '') // Remove (0-25 lbs) etc
          .trim()
          .toLowerCase()
          .replace(/\s+/g, '_'); // Replace spaces with underscores
        const key = `daily_capacity_${cleanLabel}`;
        console.log('[CapacityManager] Building key for category:', cat.category_label, '-> clean:', cleanLabel, '-> key:', key);
        // Try to find existing setting for this category
        const existingSetting = capacitySettings.find(s => s.setting_key === key);
        newSettings[key] = existingSetting ? existingSetting.setting_value : '10';
      });
      
      console.log('[CapacityManager] Final settings object:', newSettings);
      setSettings(newSettings);
    } catch (error) {
      console.error('Load data error:', error);
      console.warn('Capacity settings not configured yet');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (key, value) => {
    // Only allow positive integers
    if (value === '' || /^\d+$/.test(value)) {
      setSettings(prev => ({ ...prev, [key]: value }));
    }
  };

  const handleToggleCapacity = async () => {
    setSavingToggle(true);
    try {
      const token = sessionStorage.getItem('token');
      await axios.put(
        `${API_URL}/business-info/capacity_limits_enabled`,
        { value: (!capacityEnabled).toString() },
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      setCapacityEnabled(!capacityEnabled);
      toast.success(`Daily capacity limits ${!capacityEnabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      console.error('Toggle capacity error:', error);
      toast.error('Failed to update capacity setting');
    } finally {
      setSavingToggle(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const settingsArray = Object.entries(settings).map(([key, value]) => ({
        key,
        value: value || '0',
        description: `Maximum ${key.replace('daily_capacity_', '').replace('_', ' ')} appointments per day`
      }));

      const response = await adminApi.updateSettings(settingsArray);
      console.log('Settings update response:', response);
      toast.success('Capacity limits updated successfully!');
      // Reload data to confirm
      await loadData();
    } catch (error) {
      console.error('Settings update error:', error);
      toast.error(`Failed to update capacity limits: ${error.response?.data?.message || error.message}`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  // Get label for a size category
  const getSizeLabel = (categoryLabel) => {
    const category = sizeCategories.find(c => c.category_label === categoryLabel);
    
    if (category) {
      // Just return the category_label which already has the full display text
      return category.category_label;
    }
    // Fallback - capitalize and format the name
    const formatted = categoryLabel
      .replace('daily_capacity_', '')
      .replace(/_/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
    return formatted;
  };

  return (
    <div className="card">
      <div className="flex items-center space-x-3 mb-6">
        <Users className="text-primary-600" size={24} />
        <h2 className="text-2xl font-bold">Daily Capacity Limits</h2>
      </div>

      {/* Enable/Disable Toggle */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <h3 className="font-semibold text-gray-900 mb-1">Enable Daily Capacity Limits</h3>
            <p className="text-sm text-gray-600">
              When enabled, the system will limit the number of appointments that can be booked per day based on the limits you set below.
              When disabled, unlimited appointments can be booked (subject to employee availability).
            </p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer ml-4">
            <input
              type="checkbox"
              checked={capacityEnabled}
              onChange={handleToggleCapacity}
              disabled={savingToggle}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
          </label>
        </div>
      </div>

      {!capacityEnabled ? (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
          <Users className="mx-auto text-yellow-600 mb-3" size={40} />
          <h3 className="text-lg font-semibold text-yellow-900 mb-2">
            Daily Capacity Limits are Disabled
          </h3>
          <p className="text-yellow-800">
            Enable the toggle above to set and enforce daily capacity limits for appointments.
          </p>
        </div>
      ) : !sizingEnabled ? (
        <>
          <p className="text-gray-600 mb-6">
            Set the maximum number of appointments that can be booked per day.
          </p>

          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div className="flex-1">
                <label className="font-medium text-gray-900">Standard Daily Capacity</label>
                <p className="text-sm text-gray-500 mt-1">
                  Maximum appointments per day (all sizes)
                </p>
              </div>
              <div className="flex items-center space-x-3">
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={settings['daily_capacity_standard'] || '20'}
                  onChange={(e) => handleChange('daily_capacity_standard', e.target.value)}
                  className="w-20 px-3 py-2 border border-gray-300 rounded-lg text-center font-semibold text-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                <span className="text-gray-600">per day</span>
              </div>
            </div>
          </div>
          <p className="text-sm text-gray-500 mt-4">
            💡 Size-based pricing is disabled. This limit applies to all appointments regardless of size.
          </p>
        </>
      ) : sizeCategories.filter(c => c.is_active).length === 0 ? (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-yellow-800 font-medium">
            ⚠️ No active size categories found.
          </p>
          <p className="text-yellow-700 text-sm mt-2">
            Add and activate size categories in the Size Categories settings to configure daily capacity limits.
          </p>
        </div>
      ) : (
        <>
          <p className="text-gray-600 mb-6">
            Set the maximum number of appointments of each size that can be booked per day. 
            When a size reaches its limit, clients won't be able to book that size for the day.
          </p>

          <div className="space-y-4">
            {sizeCategories
              .filter(cat => cat.is_active)
              .sort((a, b) => a.display_order - b.display_order)
              .map(category => {
                // Create the same clean key as in loadData
                const cleanLabel = category.category_label
                  .replace(/\s*\([^)]*\)/g, '')
                  .trim()
                  .toLowerCase()
                  .replace(/\s+/g, '_');
                const key = `daily_capacity_${cleanLabel}`;
                const value = settings[key] || '10';
                
                return (
                  <div key={key} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <label className="font-medium text-gray-900">{getSizeLabel(category.category_label)}</label>
                      <p className="text-sm text-gray-500 mt-1">
                        Maximum appointments per day
                      </p>
                    </div>
                    <div className="flex items-center space-x-3">
                      <input
                        type="number"
                        min="0"
                        max="50"
                        value={value}
                        onChange={(e) => handleChange(key, e.target.value)}
                        className="w-20 px-3 py-2 border border-gray-300 rounded-lg text-center font-semibold text-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                      <span className="text-gray-600">per day</span>
                    </div>
                  </div>
                );
              })}
          </div>
        </>
      )}

      <div className="mt-6 flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className="btn btn-primary flex items-center space-x-2"
        >
          <Save size={18} />
          <span>{saving ? 'Saving...' : 'Save Changes'}</span>
        </button>
      </div>

      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <h3 className="font-semibold text-blue-900 mb-2">💡 How it works:</h3>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• Limits apply to confirmed and pending appointments</li>
          <li>• Clients will see a message if capacity is reached for that size</li>
          <li>• Changes take effect immediately for new bookings</li>
          <li>• Set to 0 to prevent bookings for that size</li>
        </ul>
      </div>
    </div>
  );
};

export default CapacityManager;
